import React from "react";
import { format } from "date-fns";
import { CommandXFooter } from "./CommandXFooter";
import type { EstimatePrintSettings } from "@/hooks/useEstimates";
import {
  DEFAULT_COMMERCIAL_EXCLUSIONS,
  DEFAULT_CLARIFICATIONS,
  parseProposalNotes,
  isAlternateGroup,
  parseLocation,
} from "@/lib/estimateProposal";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

export interface EstimatePrintSubLine {
  type: "labor" | "material";
  description: string;
}

export interface EstimatePrintLineItem {
  line_number: number;
  label?: string;
  description: string;
  customer_description?: string;
  quantity: number;
  unit: string;
  labor_unit_price: number;
  material_unit_price: number;
  unit_price: number;
  amount: number;
  location?: string;
  sub_lines?: EstimatePrintSubLine[];
}

export interface EstimatePrintGroup {
  name: string;
  color?: string;
  lines: EstimatePrintLineItem[];
  subtotal: number;
}

export interface EstimatePrintData {
  estimate_number: string;
  estimate_date: string;
  expiry_date?: string | null;
  status: string;
  customer_name?: string;
  customer_contact_name?: string;
  customer_phone?: string;
  customer_email?: string;
  project_name?: string;
  project_address?: string;
  company_name?: string;
  company_logo_url?: string | null;
  company_address?: string;
  company_city_state_zip?: string;
  company_phone?: string;
  company_website?: string;
  estimator_name?: string;
  groups?: EstimatePrintGroup[];
  flat_lines?: EstimatePrintLineItem[];
  subtotal: number;
  tax_amount: number;
  tax_rate: number;
  total: number;
  notes?: string;
  settings: EstimatePrintSettings;
  scope_summary?: string[];
  clarifications?: string[];
  exclusions?: string[];
  acceptance_text?: string;
  labor_subtotal?: number;
  material_subtotal_pre_tax?: number;
  sales_tax_total?: number;
}

const thStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "10px 8px",
  fontSize: "11px",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  fontWeight: 600,
  color: "#666",
  borderBottom: "2px solid #ddd",
};

const tdStyle: React.CSSProperties = {
  padding: "10px 8px",
  borderBottom: "1px solid #eee",
};

function LineItemsTable({
  lines,
  settings,
}: {
  lines: EstimatePrintLineItem[];
  settings: EstimatePrintSettings;
}) {
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
      <colgroup>
        <col style={{ width: "34px" }} />
        <col />
        {settings.show_quantities && <col style={{ width: "70px" }} />}
        {settings.show_quantities && <col style={{ width: "44px" }} />}
        <col style={{ width: "110px" }} />
      </colgroup>
      <thead>
        <tr>
          <th style={thStyle}>Item</th>
          <th style={thStyle}>Description</th>
          {settings.show_quantities && (
            <th style={{ ...thStyle, textAlign: "right" }}>Qty</th>
          )}
          {settings.show_quantities && (
            <th style={{ ...thStyle, textAlign: "center" }}>Unit</th>
          )}
          <th style={{ ...thStyle, textAlign: "right" }}>Amount</th>
        </tr>
      </thead>
      <tbody>
        {lines.map((li) => {
          const desc = li.customer_description || li.description;
          const code = String(li.line_number).padStart(2, "0");
          const subLines = li.sub_lines ?? [];
          return (
            <React.Fragment key={li.line_number}>
              <tr>
                <td style={{ ...tdStyle, color: "#666", fontFamily: "monospace" }}>{code}</td>
                <td style={{ ...tdStyle, fontWeight: 600, wordBreak: "break-word", overflowWrap: "anywhere" }}>
                  {li.label && <strong style={{ marginRight: 6 }}>{li.label}</strong>}
                  {desc}
                </td>
                {settings.show_quantities && (
                  <td style={{ ...tdStyle, textAlign: "right", fontFamily: "monospace" }}>
                    {new Intl.NumberFormat("en-US").format(li.quantity)}
                  </td>
                )}
                {settings.show_quantities && (
                  <td style={{ ...tdStyle, textAlign: "center", color: "#666" }}>{li.unit}</td>
                )}
                <td style={{ ...tdStyle, textAlign: "right", fontWeight: 700, fontFamily: "monospace" }}>{fmt(li.amount)}</td>
              </tr>
              {settings.show_labor_material_breakdown && subLines.map((sl, si) => (
                <tr key={si}>
                  <td style={{ ...tdStyle, borderBottom: si === subLines.length - 1 ? "1px solid #ddd" : "none" }}></td>
                  <td
                    colSpan={settings.show_quantities ? 3 : 1}
                    style={{
                      padding: "4px 8px 4px 28px",
                      fontSize: "10px",
                      color: "#888",
                      borderBottom: si === subLines.length - 1 ? "1px solid #ddd" : "none",
                    }}
                  >
                    <span style={{
                      display: "inline-block",
                      padding: "1px 5px",
                      borderRadius: "3px",
                      fontSize: "9px",
                      fontWeight: 600,
                      marginRight: "6px",
                      backgroundColor: sl.type === "labor" ? "#dbeafe" : "#fef3c7",
                      color: sl.type === "labor" ? "#1d4ed8" : "#92400e",
                    }}>
                      {sl.type === "labor" ? "Labor" : "Material"}
                    </span>
                    {sl.description} <span style={{ fontStyle: "italic" }}>(Included)</span>
                  </td>
                  <td style={{ borderBottom: si === subLines.length - 1 ? "1px solid #ddd" : "none" }}></td>
                </tr>
              ))}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
}

function SectionHeader({ name, color, isAlternate, subtotal }: { name: string; color?: string; isAlternate: boolean; subtotal: number }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "10px 12px",
        borderTop: "2px solid #111",
        borderBottom: "1px solid #111",
        backgroundColor: color ? `${color}12` : "#f8f9fa",
        marginTop: "4px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        {color && (
          <div style={{ width: "14px", height: "14px", borderRadius: "3px", backgroundColor: color }} />
        )}
        <span style={{ fontSize: "13px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#111" }}>
          {name}
        </span>
        {isAlternate && (
          <span style={{ fontSize: "9px", padding: "2px 6px", borderRadius: "3px", background: "#fff3cd", color: "#92400e", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", border: "1px solid #f59e0b" }}>
            Alternate
          </span>
        )}
      </div>
      <span style={{ fontSize: "13px", fontWeight: 700, fontFamily: "monospace", color: "#111" }}>
        {fmt(subtotal)}
      </span>
    </div>
  );
}

export const EstimatePrintTemplate = React.forwardRef<HTMLDivElement, { data: EstimatePrintData }>(({ data }, ref) => {
  const { settings } = data;
  const allGroups = data.groups ?? [];
  const baseGroups = allGroups.filter((g) => !isAlternateGroup(g.name));
  const alternateGroups = allGroups.filter((g) => isAlternateGroup(g.name));
  const baseTotal = baseGroups.reduce((s, g) => s + g.subtotal, 0) || data.subtotal;
  const alternatesTotal = alternateGroups.reduce((s, g) => s + g.subtotal, 0);

  // Parse notes into structured proposal sections
  const parsed = parseProposalNotes(data.notes);
  const scopeText = parsed.scope || parsed.remainder || "";
  // Use explicit preset data if provided, else fall back to parsed notes, then defaults
  const clarifications = data.clarifications?.length ? data.clarifications
    : parsed.clarifications.length > 0 ? parsed.clarifications : DEFAULT_CLARIFICATIONS;
  const exclusions = data.exclusions?.length ? data.exclusions
    : parsed.exclusions.length > 0 ? parsed.exclusions : DEFAULT_COMMERCIAL_EXCLUSIONS;
  const acceptanceText = data.acceptance_text || "The undersigned accepts this Proposal and authorizes the work described herein. Payment terms are Net 30 from date of invoice unless otherwise specified in the governing subcontract agreement. This Proposal shall be deemed incorporated into any resulting contract or work order.";
  const hasTaxBreakdown =
    typeof data.material_subtotal_pre_tax === "number" &&
    typeof data.labor_subtotal === "number" &&
    typeof data.sales_tax_total === "number";

  return (
    <div
      ref={ref}
      style={{
        padding: "40px 40px",
        maxWidth: "8.5in",
        margin: "0 auto",
        fontFamily: "'Times New Roman', Georgia, serif",
        fontSize: "12px",
        lineHeight: 1.55,
        color: "#111",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          borderBottom: "3px double #111",
          paddingBottom: "18px",
          marginBottom: "24px",
        }}
      >
        <div style={{ maxWidth: "55%" }}>
          {data.company_logo_url && (
            <img
              src={data.company_logo_url}
              alt={data.company_name ?? "Company"}
              style={{ maxHeight: "56px", objectFit: "contain", marginBottom: "10px" }}
            />
          )}
          {data.company_name && (
            <p style={{ fontSize: "16px", fontWeight: 700, color: "#111", margin: 0, letterSpacing: "0.02em" }}>{data.company_name}</p>
          )}
          {data.company_address && (
            <p style={{ fontSize: "11px", color: "#555", margin: "2px 0" }}>{data.company_address}</p>
          )}
          {data.company_city_state_zip && (
            <p style={{ fontSize: "11px", color: "#555", margin: "2px 0" }}>{data.company_city_state_zip}</p>
          )}
          {data.company_phone && (
            <p style={{ fontSize: "11px", color: "#555", margin: "2px 0" }}>{data.company_phone}</p>
          )}
          {data.company_website && (
            <p style={{ fontSize: "11px", color: "#1e3a5f", margin: "2px 0" }}>{data.company_website}</p>
          )}
        </div>
        <div style={{ textAlign: "right" }}>
          <h1 style={{ fontSize: "32px", fontWeight: 800, letterSpacing: "-0.01em", color: "#111", margin: "0" }}>
            ESTIMATE
          </h1>
          <p style={{ fontSize: "14px", fontWeight: 700, fontFamily: "'Courier New', monospace", color: "#222", margin: "6px 0 0" }}>
            {data.estimate_number}
          </p>
          <p style={{ color: "#555", margin: "4px 0 0", fontSize: "11px" }}>
            Date Issued: {format(new Date(data.estimate_date), "MMMM d, yyyy")}
          </p>
          {settings.show_expiry && data.expiry_date && (
            <p style={{ color: "#555", margin: "2px 0 0", fontSize: "11px" }}>
              Valid Through: {format(new Date(data.expiry_date), "MMMM d, yyyy")}
            </p>
          )}
          {data.estimator_name && (
            <p style={{ color: "#555", margin: "2px 0 0", fontSize: "11px" }}>
              Estimator: {data.estimator_name}
            </p>
          )}
        </div>
      </div>

      {/* Prepared For / Project Location */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "32px", marginBottom: "28px" }}>
        <div>
          <p style={{ fontSize: "9px", textTransform: "uppercase", letterSpacing: "0.12em", fontWeight: 700, color: "#666", marginBottom: "4px" }}>
            Prepared For
          </p>
          <p style={{ fontWeight: 700, fontSize: "14px", color: "#111", margin: 0 }}>{data.customer_name || "—"}</p>
          {data.customer_contact_name && (
            <p style={{ fontSize: "11px", color: "#333", margin: "2px 0 0" }}>Attn: {data.customer_contact_name}</p>
          )}
          {data.project_address && (
            <p style={{ fontSize: "11px", color: "#555", margin: "2px 0 0" }}>{data.project_address}</p>
          )}
          {(data.customer_phone || data.customer_email) && (
            <p style={{ fontSize: "11px", color: "#555", margin: "2px 0 0" }}>
              {[data.customer_phone, data.customer_email].filter(Boolean).join(" · ")}
            </p>
          )}
        </div>
        <div>
          <p style={{ fontSize: "9px", textTransform: "uppercase", letterSpacing: "0.12em", fontWeight: 700, color: "#666", marginBottom: "4px" }}>
            Project
          </p>
          {data.project_name && (
            <p style={{ fontWeight: 600, fontSize: "13px", color: "#111", margin: "0 0 2px" }}>{data.project_name}</p>
          )}
          <p style={{ fontWeight: 500, fontSize: "12px", color: "#555", margin: 0, whiteSpace: "pre-wrap" }}>
            {data.project_address || "Address on file"}
          </p>
        </div>
      </div>

      {/* Scope Summary */}
      {scopeText && (
        <div style={{ marginBottom: "24px", borderLeft: "4px solid #111", paddingLeft: "14px" }}>
          <p style={{ fontSize: "9px", textTransform: "uppercase", letterSpacing: "0.12em", fontWeight: 700, color: "#666", marginBottom: "4px" }}>
            Scope Summary
          </p>
          <p style={{ fontSize: "12px", color: "#222", whiteSpace: "pre-wrap", margin: 0, lineHeight: 1.6 }}>{scopeText}</p>
        </div>
      )}

      {/* BASE BID SECTION — full line items (or fallback when group_subtotals_only but no groups) */}
      {settings.show_line_items && (!settings.show_group_subtotals_only || allGroups.length === 0) && baseGroups.length > 0 && (
        <div style={{ marginBottom: "18px" }}>
          <h2 style={{ fontSize: "12px", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.14em", color: "#111", borderBottom: "2px solid #111", paddingBottom: "4px", margin: "0 0 8px" }}>
            Base Bid — Scope of Work
          </h2>
          {baseGroups.map((group, gi) => (
            <div key={gi} style={{ marginBottom: "14px" }}>
              <SectionHeader name={group.name} color={group.color} isAlternate={false} subtotal={group.subtotal} />
              <LineItemsTable lines={group.lines} settings={settings} />
            </div>
          ))}
        </div>
      )}

      {/* Flat line items fallback (ungrouped or no groups) */}
      {settings.show_line_items && (!settings.show_group_subtotals_only || allGroups.length === 0) && data.flat_lines && data.flat_lines.length > 0 && (
        <div style={{ marginBottom: "18px" }}>
          {baseGroups.length === 0 && (
            <h2 style={{ fontSize: "12px", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.14em", color: "#111", borderBottom: "2px solid #111", paddingBottom: "4px", margin: "0 0 8px" }}>
              Base Bid — Scope of Work
            </h2>
          )}
          {baseGroups.length > 0 && (
            <SectionHeader name="General" isAlternate={false} subtotal={data.flat_lines.reduce((s, l) => s + l.amount, 0)} />
          )}
          <LineItemsTable lines={data.flat_lines} settings={settings} />
        </div>
      )}

      {/* Group Subtotals Only mode */}
      {settings.show_group_subtotals_only && allGroups.length > 0 && (
        <div style={{ marginBottom: "24px" }}>
          <h2 style={{ fontSize: "12px", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.14em", color: "#111", borderBottom: "2px solid #111", paddingBottom: "4px", margin: "0 0 8px" }}>
            Base Bid — Scope Summary
          </h2>
          <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
            <colgroup>
              <col />
              <col style={{ width: "120px" }} />
            </colgroup>
            <thead>
              <tr>
                <th style={thStyle}>Section</th>
                <th style={{ ...thStyle, textAlign: "right" }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {baseGroups.map((group, gi) => (
                <tr key={gi}>
                  <td style={{ ...tdStyle, fontWeight: 500, wordBreak: "break-word", overflowWrap: "anywhere" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      {group.color && (
                        <div style={{ width: "10px", height: "10px", borderRadius: "2px", backgroundColor: group.color }} />
                      )}
                      {group.name}
                    </div>
                  </td>
                  <td style={{ ...tdStyle, textAlign: "right", fontWeight: 600, fontFamily: "monospace" }}>{fmt(group.subtotal)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ALTERNATES SECTION */}
      {settings.show_line_items && !settings.show_group_subtotals_only && alternateGroups.length > 0 && (
        <div style={{ marginBottom: "20px" }}>
          <h2 style={{ fontSize: "12px", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.14em", color: "#111", borderBottom: "2px solid #111", paddingBottom: "4px", margin: "0 0 6px" }}>
            Alternates — Add / Deduct
          </h2>
          <p style={{ fontSize: "10px", color: "#666", fontStyle: "italic", margin: "0 0 8px" }}>
            The following items are offered as alternates and are not included in the Base Bid above.
          </p>
          {alternateGroups.map((group, gi) => (
            <div key={gi} style={{ marginBottom: "14px" }}>
              <SectionHeader name={group.name} color={group.color} isAlternate subtotal={group.subtotal} />
              <LineItemsTable lines={group.lines} settings={settings} />
            </div>
          ))}
        </div>
      )}

      {/* PRICING SUMMARY */}
      <div style={{ marginTop: "22px", marginBottom: "24px", border: "2px solid #111" }}>
        <div style={{ backgroundColor: "#f8f9fa", padding: "8px 14px", borderBottom: "1px solid #111" }}>
          <p style={{ fontSize: "9px", textTransform: "uppercase", letterSpacing: "0.14em", fontWeight: 800, margin: 0 }}>
            Pricing Summary
          </p>
        </div>
        <div style={{ padding: "12px 14px" }}>
          {baseGroups.length > 0 ? (
            baseGroups.map((g, gi) => (
              <div key={gi} style={{ display: "flex", justifyContent: "space-between", padding: "3px 0", fontSize: "12px" }}>
                <span style={{ color: "#555" }}>{g.name}</span>
                <span style={{ fontFamily: "monospace", color: "#111" }}>{fmt(g.subtotal)}</span>
              </div>
            ))
          ) : !hasTaxBreakdown ? (
            <div style={{ display: "flex", justifyContent: "space-between", padding: "3px 0", fontSize: "12px" }}>
              <span style={{ color: "#555" }}>Subtotal</span>
              <span style={{ fontFamily: "monospace", color: "#111" }}>{fmt(data.subtotal)}</span>
            </div>
          ) : null}
          {hasTaxBreakdown && (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", padding: baseGroups.length > 0 ? "6px 0 3px" : "3px 0", fontSize: "12px", borderTop: baseGroups.length > 0 ? "1px solid #eee" : "none", marginTop: baseGroups.length > 0 ? "4px" : "0" }}>
                <span style={{ color: "#555" }}>Material Subtotal (pre-tax)</span>
                <span style={{ fontFamily: "monospace", color: "#111" }}>{fmt(data.material_subtotal_pre_tax)}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "3px 0", fontSize: "12px" }}>
                <span style={{ color: "#555" }}>Sales Tax ({data.tax_rate}%)</span>
                <span style={{ fontFamily: "monospace", color: "#111" }}>{fmt(data.sales_tax_total)}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "3px 0", fontSize: "12px" }}>
                <span style={{ color: "#555" }}>Labor Subtotal</span>
                <span style={{ fontFamily: "monospace", color: "#111" }}>{fmt(data.labor_subtotal)}</span>
              </div>
            </>
          )}
          {!hasTaxBreakdown && settings.show_tax && data.tax_amount > 0 && (
            <div style={{ display: "flex", justifyContent: "space-between", padding: "3px 0", fontSize: "12px", borderTop: "1px solid #eee", marginTop: "4px", paddingTop: "6px" }}>
              <span style={{ color: "#555" }}>Sales Tax ({data.tax_rate}%)</span>
              <span style={{ fontFamily: "monospace", color: "#111" }}>{fmt(data.tax_amount)}</span>
            </div>
          )}
          {!hasTaxBreakdown && settings.show_tax && data.tax_amount === 0 && data.tax_rate > 0 && (
            <div style={{ padding: "3px 0", fontSize: "10px", color: "#888", fontStyle: "italic", borderTop: "1px solid #eee", marginTop: "4px", paddingTop: "6px" }}>
              Prices include applicable sales tax ({data.tax_rate}%)
            </div>
          )}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              padding: "10px 0 4px",
              marginTop: "8px",
              fontSize: "16px",
              fontWeight: 800,
              borderTop: "2px solid #111",
              letterSpacing: "0.02em",
            }}
          >
            <span>TOTAL BASE BID</span>
            <span style={{ fontFamily: "monospace" }}>{fmt(data.total)}</span>
          </div>
          {alternateGroups.length > 0 && (
            <div style={{ marginTop: "10px", paddingTop: "8px", borderTop: "1px dashed #999" }}>
              <p style={{ fontSize: "9px", textTransform: "uppercase", letterSpacing: "0.12em", fontWeight: 700, color: "#666", margin: "0 0 4px" }}>
                Alternates (not included above)
              </p>
              {alternateGroups.map((g, gi) => (
                <div key={gi} style={{ display: "flex", justifyContent: "space-between", padding: "2px 0", fontSize: "11px" }}>
                  <span style={{ color: "#555" }}>{g.name}</span>
                  <span style={{ fontFamily: "monospace", color: "#111" }}>{fmt(g.subtotal)}</span>
                </div>
              ))}
              <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0 0", marginTop: "4px", borderTop: "1px solid #ddd", fontSize: "11px", fontWeight: 600 }}>
                <span>Total Alternates</span>
                <span style={{ fontFamily: "monospace" }}>{fmt(alternatesTotal)}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* CLARIFICATIONS */}
      {clarifications.length > 0 && (
        <div style={{ marginBottom: "18px" }}>
          <h2 style={{ fontSize: "11px", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.14em", color: "#111", borderBottom: "1px solid #111", paddingBottom: "4px", margin: "0 0 8px" }}>
            Clarifications & Assumptions
          </h2>
          <ul style={{ margin: 0, paddingLeft: "20px", fontSize: "11px", color: "#222" }}>
            {clarifications.map((c, i) => (
              <li key={i} style={{ marginBottom: "4px", lineHeight: 1.55 }}>{c}</li>
            ))}
          </ul>
        </div>
      )}

      {/* EXCLUSIONS */}
      {exclusions.length > 0 && (
        <div style={{ marginBottom: "24px" }}>
          <h2 style={{ fontSize: "11px", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.14em", color: "#111", borderBottom: "1px solid #111", paddingBottom: "4px", margin: "0 0 8px" }}>
            Exclusions
          </h2>
          <ul style={{ margin: 0, paddingLeft: "20px", fontSize: "11px", color: "#222" }}>
            {exclusions.map((e, i) => (
              <li key={i} style={{ marginBottom: "4px", lineHeight: 1.55 }}>{e}</li>
            ))}
          </ul>
        </div>
      )}

      {/* ACCEPTANCE */}
      <div style={{ marginTop: "28px", paddingTop: "16px", borderTop: "2px solid #111" }}>
        <p style={{ fontSize: "11px", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.14em", color: "#111", margin: "0 0 8px" }}>
          Acceptance
        </p>
        <p style={{ fontSize: "11px", color: "#222", lineHeight: 1.6, margin: "0 0 12px" }}>
          {acceptanceText}
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "40px", marginTop: "32px" }}>
          <div>
            <div style={{ borderBottom: "1px solid #111", height: "24px" }} />
            <p style={{ fontSize: "9px", color: "#666", margin: "4px 0 0", textTransform: "uppercase", letterSpacing: "0.08em" }}>
              Authorized Signature
            </p>
          </div>
          <div>
            <div style={{ borderBottom: "1px solid #111", height: "24px" }} />
            <p style={{ fontSize: "9px", color: "#666", margin: "4px 0 0", textTransform: "uppercase", letterSpacing: "0.08em" }}>
              Printed Name & Date
            </p>
          </div>
        </div>
      </div>

      <CommandXFooter />
    </div>
  );
});

EstimatePrintTemplate.displayName = "EstimatePrintTemplate";
